const RETURN_TO_BASE_URL = "https://workus.local";

export interface NewTaskSearchParams {
  prompt?: string;
  agent?: string;
}

export function sanitizeAuthReturnTo(value: string | null | undefined): string | undefined {
  if (!value) {
    return undefined;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue.startsWith("/") || trimmedValue.startsWith("//")) {
    return undefined;
  }

  try {
    const parsed = new URL(trimmedValue, RETURN_TO_BASE_URL);
    if (parsed.origin !== RETURN_TO_BASE_URL) {
      return undefined;
    }

    if (!parsed.pathname.startsWith("/workspace/")) {
      return undefined;
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return undefined;
  }
}

export function getAuthReturnToFromRequestUrl(requestUrl: string): string | undefined {
  try {
    const url = new URL(requestUrl);
    return sanitizeAuthReturnTo(`${url.pathname}${url.search}`);
  } catch {
    return undefined;
  }
}

export function buildSigninRedirectSearch(requestUrl: string): { return_to?: string } {
  const returnTo = getAuthReturnToFromRequestUrl(requestUrl);
  return returnTo ? { return_to: returnTo } : {};
}

export function buildOnboardingReturnPath(returnTo: string | undefined): string {
  const safeReturnTo = sanitizeAuthReturnTo(returnTo);
  if (!safeReturnTo) {
    return "/onboarding";
  }

  const searchParams = new URLSearchParams({ return_to: safeReturnTo });
  return `/onboarding?${searchParams.toString()}`;
}

export function buildWorkspaceNewTaskReturnTo(search: NewTaskSearchParams): string {
  const searchParams = new URLSearchParams();
  if (search.prompt?.trim()) {
    searchParams.set("prompt", search.prompt);
  }
  if (search.agent?.trim()) {
    searchParams.set("agent", search.agent);
  }

  const query = searchParams.toString();
  return query ? `/workspace/new-task?${query}` : "/workspace/new-task";
}

export function parseNewTaskSearch(search: Record<string, unknown>): NewTaskSearchParams {
  return {
    prompt: typeof search.prompt === "string" ? search.prompt : undefined,
    agent: typeof search.agent === "string" ? search.agent : undefined,
  };
}

export function extractNewTaskSearchFromReturnTo(
  returnTo: string | undefined,
): NewTaskSearchParams {
  if (!returnTo) {
    return {};
  }

  try {
    const parsed = new URL(returnTo, RETURN_TO_BASE_URL);
    if (!parsed.pathname.endsWith("/new-task")) {
      return {};
    }

    const prompt = parsed.searchParams.get("prompt")?.trim();
    const agent = parsed.searchParams.get("agent")?.trim();
    return {
      ...(prompt ? { prompt } : {}),
      ...(agent ? { agent } : {}),
    };
  } catch {
    return {};
  }
}
